import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/apiClient";
import { toast } from "sonner";
import { FileText, Headphones, Presentation, Check, X, CalendarClock } from "lucide-react";

const Flag = ({ ok, Icon, label }) => (
  <span title={`${label}: ${ok ? "carregado" : "em falta"}`}
    className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] border ${
      ok ? "border-[#1F3F1F] text-[#7BD47B] bg-[#0C170C]" : "border-[#3F1F1F] text-[#E57373] bg-[#170C0C]"}`}>
    <Icon className="w-3 h-3" />{ok ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
  </span>
);

function ClubColumn({ c }) {
  return (
    <div className="kw-card p-5 space-y-4" data-testid={`map-club-${c.club}`}>
      <div className="flex items-baseline justify-between gap-3 border-b border-[#1F1F1F] pb-3">
        <h2 className="font-display text-base md:text-lg font-black uppercase tracking-tight">{c.club}</h2>
        <span className="text-[10px] uppercase tracking-widest text-[#737373]" data-testid={`map-current-${c.club}`}>
          Agora: Sessão {c.current_session_no}
        </span>
      </div>
      <div className="space-y-1.5">
        {c.weeks.map((w) => (
          <div key={w.week} data-testid={`map-row-${c.club}-${w.week}`}
            className={`flex items-center gap-3 px-2 py-1.5 border-l-2 transition-colors ${
              w.current ? "border-[#E50000] bg-[#140909]" : w.past ? "border-[#1F1F1F] opacity-55" : "border-[#2A2A2A] hover:bg-[#0E0E0E]"}`}>
            <span className="font-display text-sm font-black w-10 shrink-0">S{w.session_no}</span>
            <span className="text-xs text-[#A3A3A3] w-28 shrink-0">
              {w.date ? `${w.weekday} ${w.date}` : "sem data"}
              <span className="block text-[10px] text-[#555]">Semana {w.week}{w.time ? ` · ${w.time}` : ""}</span>
            </span>
            <span className="text-xs truncate flex-1" title={w.title}>{w.title}</span>
            {w.not_held && <span className="text-[10px] uppercase tracking-widest text-[#E5A000]">não se realizou</span>}
            {w.material_sent && <span className="text-[10px] uppercase tracking-widest text-[#7BD47B]">enviado</span>}
            <span className="flex items-center gap-1 shrink-0">
              <Flag ok={w.has_pdf} Icon={FileText} label="PDF" />
              <Flag ok={w.has_audio} Icon={Headphones} label="Áudio" />
              <Flag ok={w.has_pptx} Icon={Presentation} label="PowerPoint" />
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function SessionsMap() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/weekly-sessions/map").then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e)));
  }, []);

  if (!data) return <div className="kw-overline">A carregar…</div>;
  return (
    <div className="space-y-8 kw-fade-up" data-testid="sessions-map-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="kw-overline mb-2">Auto-Treino · Semana {data.current_week}</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tighter">
            Mapa das <span className="text-[#E50000]">sessões</span>
          </h1>
          <p className="text-sm text-[#A3A3A3] mt-2 max-w-3xl">
            Em que sessão vai cada clube e o que falta carregar. Cada clube segue o seu ritmo: se um ficou atrasado,
            acerta em «Esta reunião é a Sessão…» nas Reuniões semanais e as seguintes empurram-se sozinhas.
          </p>
        </div>
        <Link to="/reunioes-semanais" className="kw-btn-secondary !py-2 !px-4 text-xs inline-flex items-center gap-2"
          data-testid="map-go-sessions">
          <CalendarClock className="w-4 h-4" /> Reuniões semanais
        </Link>
      </div>
      <div className="grid lg:grid-cols-2 gap-4">
        {data.clubs.map((c) => <ClubColumn key={c.club} c={c} />)}
      </div>
    </div>
  );
}
